import { useCallback, useState } from "react";

/**
 * Shared submit-state hook for the footer feedback / suggest-a-spot
 * modals. POSTs JSON to `/api/feedback` and exposes a four-state
 * machine the modals can render off:
 *   - `idle`     — form is editable
 *   - `sending`  — request in flight
 *   - `sent`     — server returned 2xx
 *   - `error`    — request or server failed
 *
 * Both modals share the exact same submit shape, so this lives in
 * `src/lib` instead of being duplicated in each component.
 */

export type SubmitState = "idle" | "sending" | "sent" | "error";

export interface FeedbackPayload {
  kind: "feedback" | "suggest_spot";
  message: string;
  email?: string;
  spotName?: string;
  location?: string;
  directions?: readonly string[];
}

export function useSubmitFeedback() {
  const [state, setState] = useState<SubmitState>("idle");

  const submit = useCallback(async (payload: FeedbackPayload) => {
    setState("sending");
    try {
      const resp = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setState("sent");
    } catch {
      setState("error");
    }
  }, []);

  const reset = useCallback(() => setState("idle"), []);

  return { state, submit, reset };
}
